// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import useCaseService from "../../services/useCaseService";

const emptyConfig = {
  is_enabled: true,
  pricing_rules: { base_rate: "", hourly_rate: "" },
  time_restrictions: { min_minutes: "", max_minutes: "" },
  max_concurrent_bookings: 1,
};

const SpaceUseCaseConfig = ({ spaceId, useCases }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!spaceId) {
      setRows([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    useCaseService
      .getSpaceUseCases(spaceId)
      .then((resp) => {
        if (cancelled) return;
        const items = Array.isArray(resp)
          ? resp
          : (resp?.items ?? resp?.data ?? resp?.results ?? []);
        setRows(
          items.map((r) => ({
            use_case_id: r.use_case_id ?? r.use_case?.id,
            is_enabled: r.is_enabled !== false,
            pricing_rules: r.pricing_rules || {
              base_rate: "",
              hourly_rate: "",
            },
            time_restrictions: r.time_restrictions || {
              min_minutes: "",
              max_minutes: "",
            },
            max_concurrent_bookings: r.max_concurrent_bookings ?? 1,
            _persisted: true,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [spaceId]);

  const useCasesById = useMemo(
    () =>
      (useCases || []).reduce((acc, uc) => {
        acc[uc.id] = uc;
        return acc;
      }, {}),
    [useCases],
  );

  if (!spaceId) {
    return (
      <p className="text-xs text-amber-600 dark:text-amber-400">
        Bitte zuerst die Fläche speichern, um Nutzungsarten zu konfigurieren.
      </p>
    );
  }

  const assignedIds = new Set(rows.map((r) => r.use_case_id));
  const addable = (useCases || []).filter((uc) => !assignedIds.has(uc.id));

  const updateRow = (ucId, patch) =>
    setRows((prev) =>
      prev.map((r) => (r.use_case_id === ucId ? { ...r, ...patch } : r)),
    );

  const addRow = (ucId) => {
    const id = Number(ucId);
    if (!id || assignedIds.has(id)) return;
    setRows((prev) => [
      ...prev,
      { use_case_id: id, ...emptyConfig, _persisted: false },
    ]);
  };

  const removeRow = (ucId) =>
    setRows((prev) => prev.filter((r) => r.use_case_id !== ucId));

  const apply = async () => {
    setSaving(true);
    try {
      for (const r of rows) {
        const payload = {
          use_case_id: r.use_case_id,
          is_enabled: r.is_enabled !== false,
          pricing_rules: {
            base_rate:
              r.pricing_rules?.base_rate === ""
                ? null
                : Number(r.pricing_rules?.base_rate),
            hourly_rate:
              r.pricing_rules?.hourly_rate === ""
                ? null
                : Number(r.pricing_rules?.hourly_rate),
          },
          time_restrictions: {
            min_minutes:
              r.time_restrictions?.min_minutes === ""
                ? null
                : Number(r.time_restrictions?.min_minutes),
            max_minutes:
              r.time_restrictions?.max_minutes === ""
                ? null
                : Number(r.time_restrictions?.max_minutes),
          },
          max_concurrent_bookings: Number(r.max_concurrent_bookings) || 1,
        };
        if (r._persisted) {
          await useCaseService.updateSpaceUseCase(
            spaceId,
            r.use_case_id,
            payload,
          );
        } else {
          await useCaseService.assignSpaceUseCase(spaceId, payload);
        }
      }
      toast.success("Nutzungsart-Konfiguration übernommen.");
      setRows((prev) => prev.map((r) => ({ ...r, _persisted: true })));
    } catch (err) {
      console.error("Error saving space use-case config:", err);
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const removePersisted = async (ucId) => {
    try {
      await useCaseService.removeSpaceUseCase(spaceId, ucId);
      removeRow(ucId);
      toast.success("Nutzungsart entfernt.");
    } catch (err) {
      console.error("Error removing space use-case:", err);
      toast.error("Entfernen fehlgeschlagen.");
    }
  };

  const fieldClass =
    "w-full px-2 py-1 text-sm border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded";
  const labelClass = "block text-xs text-gray-600 dark:text-slate-400 mb-1";

  return (
    <div className="space-y-3">
      {loading ? (
        <p className="text-xs text-gray-500 dark:text-slate-400">
          Lade Nutzungsarten…
        </p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-slate-400">
          Keine Nutzungsarten konfiguriert.
        </p>
      ) : (
        rows.map((r) => {
          const meta = useCasesById[r.use_case_id];
          return (
            <div
              key={r.use_case_id}
              className="border border-gray-200 dark:border-slate-700 rounded p-2"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900 dark:text-slate-100">
                  {meta?.name || `#${r.use_case_id}`}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    r._persisted
                      ? removePersisted(r.use_case_id)
                      : removeRow(r.use_case_id)
                  }
                  className="text-xs text-red-600 hover:underline"
                >
                  entfernen
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className={labelClass}>Grundpreis</label>
                  <input
                    type="number"
                    step="0.01"
                    value={r.pricing_rules?.base_rate ?? ""}
                    onChange={(e) =>
                      updateRow(r.use_case_id, {
                        pricing_rules: {
                          ...(r.pricing_rules || {}),
                          base_rate: e.target.value,
                        },
                      })
                    }
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Stundenpreis</label>
                  <input
                    type="number"
                    step="0.01"
                    value={r.pricing_rules?.hourly_rate ?? ""}
                    onChange={(e) =>
                      updateRow(r.use_case_id, {
                        pricing_rules: {
                          ...(r.pricing_rules || {}),
                          hourly_rate: e.target.value,
                        },
                      })
                    }
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Min. Minuten</label>
                  <input
                    type="number"
                    value={r.time_restrictions?.min_minutes ?? ""}
                    onChange={(e) =>
                      updateRow(r.use_case_id, {
                        time_restrictions: {
                          ...(r.time_restrictions || {}),
                          min_minutes: e.target.value,
                        },
                      })
                    }
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Max. Minuten</label>
                  <input
                    type="number"
                    value={r.time_restrictions?.max_minutes ?? ""}
                    onChange={(e) =>
                      updateRow(r.use_case_id, {
                        time_restrictions: {
                          ...(r.time_restrictions || {}),
                          max_minutes: e.target.value,
                        },
                      })
                    }
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Max. parallele Buchungen</label>
                  <input
                    type="number"
                    min="1"
                    value={r.max_concurrent_bookings ?? 1}
                    onChange={(e) =>
                      updateRow(r.use_case_id, {
                        max_concurrent_bookings: Number(e.target.value) || 1,
                      })
                    }
                    className={fieldClass}
                  />
                </div>
                <label className="flex items-center gap-2 self-end text-xs text-gray-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={r.is_enabled !== false}
                    onChange={(e) =>
                      updateRow(r.use_case_id, { is_enabled: e.target.checked })
                    }
                  />
                  aktiv
                </label>
              </div>
            </div>
          );
        })
      )}

      <div className="flex flex-wrap items-center gap-2">
        {addable.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              addRow(e.target.value);
              e.target.value = "";
            }}
            className={fieldClass}
            style={{ maxWidth: 220 }}
          >
            <option value="">+ Nutzungsart hinzufügen…</option>
            {addable.map((uc) => (
              <option key={uc.id} value={uc.id}>
                {uc.name}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={apply}
          disabled={saving || rows.length === 0}
          className="ml-auto bg-primary-color text-white text-sm px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Speichert…" : "Übernehmen"}
        </button>
      </div>
    </div>
  );
};

export default SpaceUseCaseConfig;
